import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  MapPin, Plus, Search, X, Upload, Edit2, Trash2,
  Building2, Zap, LayoutGrid, Image,
  CheckCircle2, Move, BoxSelect,
} from 'lucide-react';
import axios from 'axios';

const BASE = process.env.REACT_APP_API_BASE_URL;
const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('mideast_token')}` }
});
const getMultipartHeader = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem('mideast_token')}`,
    'Content-Type': 'multipart/form-data',
  }
});

const STRING_COLORS = [
  '#3B82F6', '#22D3A5', '#F59E0B', '#EF4444',
  '#A855F7', '#EC4899', '#F97316', '#14B8A6',
];

// Polygon centroid for label placement
const centroid = (pts) => ({
  x: pts.reduce((s, p) => s + p.x, 0) / pts.length,
  y: pts.reduce((s, p) => s + p.y, 0) / pts.length,
});

const STYLES = `
  .ms-header {
    display: flex; align-items: center;
    justify-content: space-between;
    margin-bottom: 24px; gap: 16px;
  }
  .ms-toolbar {
    display: flex; align-items: center; gap: 12px;
    margin-bottom: 20px; flex-wrap: wrap;
  }
  .ms-search-wrap { position: relative; flex: 1; max-width: 320px; }
  .ms-search-icon {
    position: absolute; left: 12px; top: 50%;
    transform: translateY(-50%);
    color: rgba(255,255,255,0.25); pointer-events: none;
  }
  .ms-search {
    width: 100%; padding: 9px 14px 9px 38px;
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 10px; color: #E2E8F0;
    font-family: 'DM Sans', sans-serif; font-size: 13px;
    outline: none; transition: border-color 0.2s;
  }
  .ms-search:focus { border-color: rgba(14,165,233,0.35); }
  .ms-search::placeholder { color: rgba(255,255,255,0.20); }
  .ms-filter-select {
    padding: 9px 14px;
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 10px; color: #E2E8F0;
    font-family: 'DM Sans', sans-serif; font-size: 13px;
    outline: none; cursor: pointer;
  }
  .ms-filter-select option { background: #1E2535; }

  .ms-table-wrap {
    background: #0D1120;
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 14px; overflow: hidden;
  }
  .ms-diagram-badge {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 3px 9px; border-radius: 20px;
    font-size: 10px; font-weight: 700;
    font-family: 'DM Mono', monospace;
  }
  .ms-diagram-yes {
    background: rgba(34,197,94,0.10); color: #86EFAC;
    border: 1px solid rgba(34,197,94,0.20);
  }
  .ms-diagram-no {
    background: rgba(255,255,255,0.05); color: rgba(255,255,255,0.28);
    border: 1px solid rgba(255,255,255,0.08);
  }
  .ms-row-actions { display: flex; align-items: center; gap: 6px; opacity: 0; transition: opacity 0.15s; }
  tr:hover .ms-row-actions { opacity: 1; }

  /* Panel */
  .ms-panel-overlay {
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.55); backdrop-filter: blur(4px);
    z-index: 200; animation: fadeIn 0.15s ease;
  }
  .ms-panel {
    position: fixed; top: 0; right: 0; bottom: 0;
    width: 620px; max-width: 100vw;
    background: #0D1120;
    border-left: 1px solid rgba(255,255,255,0.08);
    box-shadow: -24px 0 80px rgba(0,0,0,0.50);
    z-index: 201; display: flex; flex-direction: column;
    animation: slideIn 0.22s cubic-bezier(0.4,0,0.2,1); overflow: hidden;
  }
  @keyframes slideIn {
    from { transform: translateX(100%); opacity: 0; }
    to   { transform: translateX(0);    opacity: 1; }
  }
  .ms-panel-header {
    padding: 20px 24px;
    border-bottom: 1px solid rgba(255,255,255,0.06);
    display: flex; align-items: center; gap: 12px; flex-shrink: 0;
  }
  .ms-panel-title { font-size: 16px; font-weight: 800; color: #F1F5F9; flex: 1; }
  .ms-panel-sub   { font-size: 11px; color: rgba(255,255,255,0.30); margin-top: 2px; }
  .ms-panel-body  { flex: 1; overflow-y: auto; padding: 22px 24px; }
  .ms-panel-tabs  {
    display: flex; gap: 4px; padding: 0 24px;
    border-bottom: 1px solid rgba(255,255,255,0.06); flex-shrink: 0;
  }
  .ms-panel-tab {
    padding: 12px 16px; font-size: 12px; font-weight: 700;
    color: rgba(255,255,255,0.35); cursor: pointer; border: none; background: none;
    border-bottom: 2px solid transparent; transition: all 0.18s;
    font-family: 'DM Sans', sans-serif;
  }
  .ms-panel-tab:hover { color: rgba(255,255,255,0.65); }
  .ms-panel-tab.active { color: #0EA5E9; border-bottom-color: #0EA5E9; }

  .ms-info-grid  { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 20px; }
  .ms-info-item  {
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.05);
    border-radius: 10px; padding: 12px 14px;
  }
  .ms-info-item-label {
    font-size: 9px; font-weight: 700; letter-spacing: 0.16em; text-transform: uppercase;
    color: rgba(255,255,255,0.25); font-family: 'DM Mono', monospace; margin-bottom: 5px;
  }
  .ms-info-item-value { font-size: 13px; font-weight: 600; color: #E2E8F0; }

  /* Diagram upload zone */
  .ms-diagram-upload-zone {
    border: 2px dashed rgba(14,165,233,0.25); border-radius: 14px; padding: 40px 24px;
    text-align: center; cursor: pointer; transition: all 0.2s;
    background: rgba(14,165,233,0.03);
  }
  .ms-diagram-upload-zone:hover { border-color: rgba(14,165,233,0.50); background: rgba(14,165,233,0.06); }
  .ms-diagram-upload-icon {
    width: 52px; height: 52px; border-radius: 14px;
    background: rgba(14,165,233,0.10);
    display: flex; align-items: center; justify-content: center;
    margin: 0 auto 14px; color: #38BDF8;
  }
  .ms-diagram-upload-title { font-size: 14px; font-weight: 700; color: #E2E8F0; margin-bottom: 6px; }
  .ms-diagram-upload-sub   { font-size: 12px; color: rgba(255,255,255,0.28); }

  /* Mode toggle */
  .ms-mode-toggle { display: flex; gap: 6px; margin-bottom: 14px; }
  .ms-mode-btn {
    display: flex; align-items: center; gap: 6px;
    padding: 7px 14px; border-radius: 8px;
    font-size: 11px; font-weight: 700; font-family: 'DM Mono', monospace;
    cursor: pointer; border: 1px solid rgba(255,255,255,0.08);
    background: rgba(255,255,255,0.04); color: rgba(255,255,255,0.35); transition: all 0.18s;
  }
  .ms-mode-btn.active-pins    { background: rgba(245,158,11,0.12); color: #FCD34D;  border-color: rgba(245,158,11,0.28); }
  .ms-mode-btn.active-strings { background: rgba(59,130,246,0.12); color: #93C5FD;  border-color: rgba(59,130,246,0.28); }

  /* Canvas */
  .ms-pin-canvas-wrap {
    position: relative; display: inline-block; width: 100%;
    border-radius: 10px; overflow: hidden;
    border: 1px solid rgba(14,165,233,0.20);
    user-select: none; cursor: crosshair;
  }
  .ms-pin-canvas-img { width: 100%; display: block; user-select: none; -webkit-user-drag: none; }

  /* SVG overlay */
  .ms-svg-overlay {
    position: absolute; inset: 0; width: 100%; height: 100%;
    pointer-events: none; overflow: visible;
  }

  /* String label chips on canvas */
  .ms-string-label-chip {
    position: absolute; transform: translate(-50%, -50%);
    font-size: 10px; font-weight: 800; font-family: 'DM Mono', monospace;
    border-radius: 4px; padding: 2px 7px; white-space: nowrap;
    background: rgba(0,0,0,0.70); backdrop-filter: blur(4px);
    pointer-events: none; z-index: 10;
  }

  /* Draft toolbar */
  .ms-draft-toolbar {
    display: flex; align-items: center; gap: 8px;
    padding: 10px 14px; border-radius: 10px; margin-bottom: 10px;
    background: rgba(59,130,246,0.08); border: 1px solid rgba(59,130,246,0.20);
    font-size: 11px; font-weight: 700; font-family: 'DM Mono', monospace;
    color: rgba(147,197,253,0.75);
  }

  /* Pins */
  .ms-pin { position: absolute; transform: translate(-50%, -50%); z-index: 10; cursor: pointer; }
  .ms-pin-dot {
    width: 22px; height: 22px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-size: 9px; font-weight: 800; color: #080C18;
    box-shadow: 0 0 0 3px rgba(245,158,11,0.30), 0 4px 12px rgba(0,0,0,0.50);
    transition: transform 0.15s; font-family: 'DM Mono', monospace;
  }
  .ms-pin:hover .ms-pin-dot { transform: scale(1.2); }
  .ms-pin-dot-gold { background: #F59E0B; }
  .ms-pin-tooltip {
    position: absolute; bottom: 28px; left: 50%; transform: translateX(-50%);
    background: #0D1829; border: 1px solid rgba(14,165,233,0.25);
    border-radius: 7px; padding: 5px 10px; white-space: nowrap; font-size: 11px;
    color: #E2E8F0; font-weight: 600; pointer-events: none; opacity: 0; transition: opacity 0.15s;
    box-shadow: 0 4px 14px rgba(0,0,0,0.40); z-index: 20;
  }
  .ms-pin:hover .ms-pin-tooltip { opacity: 1; }
  .ms-pin-remove {
    position: absolute; top: -6px; right: -6px; width: 14px; height: 14px;
    border-radius: 50%; background: #EF4444; color: #fff;
    display: flex; align-items: center; justify-content: center;
    font-size: 8px; opacity: 0; transition: opacity 0.15s; cursor: pointer; border: none;
  }
  .ms-pin:hover .ms-pin-remove { opacity: 1; }
  .ms-pin-list { display: flex; flex-direction: column; gap: 8px; margin-top: 16px; }
  .ms-pin-list-item {
    display: flex; align-items: center; gap: 10px; padding: 10px 12px;
    border-radius: 9px; background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.06);
  }
  .ms-pin-list-num {
    width: 22px; height: 22px; border-radius: 50%; background: #F59E0B; color: #080C18;
    display: flex; align-items: center; justify-content: center;
    font-size: 9px; font-weight: 800; flex-shrink: 0; font-family: 'DM Mono', monospace;
  }
  .ms-pin-label-input {
    flex: 1; background: transparent; border: none; outline: none;
    color: #E2E8F0; font-size: 13px; font-family: 'DM Sans', sans-serif;
    border-bottom: 1px solid rgba(255,255,255,0.08); padding: 2px 0;
  }
  .ms-pin-label-input:focus { border-bottom-color: rgba(14,165,233,0.40); }
  .ms-pin-label-input::placeholder { color: rgba(255,255,255,0.20); }
  .ms-pin-desc-input {
    width: 100%; background: transparent; border: none; outline: none;
    color: rgba(255,255,255,0.45); font-size: 11px; font-family: 'DM Sans', sans-serif;
    border-bottom: 1px solid rgba(255,255,255,0.05); padding: 2px 0; margin-top: 3px;
  }
  .ms-pin-desc-input::placeholder { color: rgba(255,255,255,0.15); }

  /* Pending string form */
  .ms-pending-form {
    background: rgba(59,130,246,0.06); border: 1px solid rgba(59,130,246,0.22);
    border-radius: 12px; padding: 16px; margin-top: 14px;
  }
  .ms-pending-form-title {
    font-size: 9px; font-weight: 700; letter-spacing: 0.16em; text-transform: uppercase;
    color: rgba(147,197,253,0.65); font-family: 'DM Mono', monospace; margin-bottom: 12px;
  }
  .ms-color-row { display: flex; align-items: center; gap: 8px; margin-bottom: 14px; }
  .ms-color-swatch {
    width: 22px; height: 22px; border-radius: 50%; cursor: pointer; flex-shrink: 0;
    border: 2px solid transparent; transition: transform 0.15s, border-color 0.15s;
  }
  .ms-color-swatch:hover  { transform: scale(1.25); }
  .ms-color-swatch.selected { border-color: rgba(255,255,255,0.75); transform: scale(1.15); }

  /* String list */
  .ms-string-list { display: flex; flex-direction: column; gap: 8px; margin-top: 16px; }
  .ms-string-item {
    display: flex; align-items: center; gap: 10px; padding: 10px 14px;
    border-radius: 9px; background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.06);
  }
  .ms-string-color-block { width: 14px; height: 14px; border-radius: 3px; flex-shrink: 0; }

  .ms-canvas-hint {
    font-size: 11px; color: rgba(255,255,255,0.28); font-family: 'DM Mono', monospace;
    text-align: center; margin: 10px 0 16px;
    display: flex; align-items: center; justify-content: center; gap: 6px;
  }
  .ms-form-grid  { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  .ms-form-full  { grid-column: 1 / -1; }
`;

const EMPTY_FORM = {
  client_id: '', site_name: '', location: '',
  system_size: '', panel_count: '', notes: '',
};

const MideastSites = () => {
  const [sites,          setSites]          = useState([]);
  const [clients,        setClients]        = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [search,         setSearch]         = useState('');
  const [filterClient,   setFilterClient]   = useState('');
  const [selected,       setSelected]       = useState(null);
  const [activeTab,      setActiveTab]      = useState('info');
  const [showForm,       setShowForm]       = useState(false);
  const [editSite,       setEditSite]       = useState(null);
  const [form,           setForm]           = useState(EMPTY_FORM);
  const [saving,         setSaving]         = useState(false);
  const [error,          setError]          = useState('');

  // Diagram / pins
  const [diagram,        setDiagram]        = useState(null);
  const [diagramLoading, setDiagramLoading] = useState(false);
  const [pins,           setPins]           = useState([]);
  const [savingPins,     setSavingPins]     = useState(false);
  const [uploadingDiag,  setUploadingDiag]  = useState(false);
  const diagramInputRef  = useRef(null);
  const canvasRef        = useRef(null);

  // Strings / polygon
  const [diagramMode,    setDiagramMode]    = useState('pins');
  const [strings,        setStrings]        = useState([]);
  const [draftPoints,    setDraftPoints]    = useState([]); // vertices being placed
  const [mousePos,       setMousePos]       = useState(null); // live cursor for preview line
  const [pendingShape,   setPendingShape]   = useState(null); // closed polygon awaiting label
  const [pendingLabel,   setPendingLabel]   = useState('');
  const [pendingColor,   setPendingColor]   = useState(STRING_COLORS[0]);
  const [savingString,   setSavingString]   = useState(false);

  // ── Data ────────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [sitesRes, clientsRes] = await Promise.all([
        axios.get(`${BASE}/mideast/sites`,   getAuthHeader()),
        axios.get(`${BASE}/mideast/clients`, getAuthHeader()),
      ]);
      setSites(sitesRes.data.sites      || []);
      setClients(clientsRes.data.clients || []);
    } catch (err) {
      console.error('[Sites] fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openDetail = useCallback((site) => {
    setSelected(site);
    setActiveTab('info');
    setDiagram(null);
    setPins([]);
    setStrings([]);
    setDiagramMode('pins');
    setDraftPoints([]);
    setMousePos(null);
    setPendingShape(null);
  }, []);

  // Load diagram + strings when diagram tab opens
  useEffect(() => {
    if (activeTab !== 'diagram' || !selected) return;
    setDiagramLoading(true);
    setDiagramMode('pins');
    setDraftPoints([]);
    setPendingShape(null);
    setStrings([]);

    axios.get(`${BASE}/mideast/diagrams/site/${selected.id}`, getAuthHeader())
      .then(res => {
        const d = res.data.diagram;
        setDiagram(d);
        setPins(d?.points ? d.points.map((p, i) => ({ ...p, _localId: i })) : []);
        setStrings(d?.strings || []);
      })
      .catch(err => console.error('[Sites] diagram fetch error:', err))
      .finally(() => setDiagramLoading(false));
  }, [activeTab, selected]);

  // ── Canvas coordinate helper ─────────────────────────────────────
  const getCursorPct = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: parseFloat(((e.clientX - rect.left) / rect.width  * 100).toFixed(2)),
      y: parseFloat(((e.clientY - rect.top)  / rect.height * 100).toFixed(2)),
    };
  };

  // ── Canvas click — handles pins AND polygon vertex placement ────
  const handleCanvasClick = (e) => {
    if (!canvasRef.current) return;
    const { x, y } = getCursorPct(e);

    if (diagramMode === 'pins') {
      setPins(prev => [...prev, {
        _localId: Date.now(), label: '', description: '',
        x_percent: x, y_percent: y, order_index: prev.length,
      }]);
      return;
    }

    if (diagramMode === 'strings') {
      if (pendingShape) return; // waiting for label
      // Close polygon if clicking near the first vertex (≥3 points placed)
      if (draftPoints.length >= 3) {
        const first = draftPoints[0];
        const dist = Math.sqrt((x - first.x) ** 2 + (y - first.y) ** 2);
        if (dist < 4) {
          setPendingShape([...draftPoints]);
          setDraftPoints([]);
          setMousePos(null);
          return;
        }
      }
      setDraftPoints(prev => [...prev, { x, y }]);
    }
  };

  // Mouse move — only used in strings mode to draw the preview line
  const handleCanvasMouseMove = (e) => {
    if (diagramMode !== 'strings' || draftPoints.length === 0 || pendingShape) return;
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    setMousePos({
      x: (e.clientX - rect.left) / rect.width  * 100,
      y: (e.clientY - rect.top)  / rect.height * 100,
    });
  };

  const finishShape = () => {
    if (draftPoints.length < 3) return;
    setPendingShape([...draftPoints]);
    setDraftPoints([]);
    setMousePos(null);
  };

  const cancelDraft = () => {
    setDraftPoints([]);
    setMousePos(null);
  };

  const cancelPendingShape = () => {
    setPendingShape(null);
    setPendingLabel('');
    setPendingColor(STRING_COLORS[0]);
  };

  // ── Save string ──────────────────────────────────────────────────
  const saveString = async () => {
    if (!pendingLabel.trim() || !pendingShape || !diagram) return;
    setSavingString(true);
    try {
      const res = await axios.post(
        `${BASE}/mideast/strings`,
        {
          diagram_id: diagram.id,
          label:      pendingLabel.trim(),
          color:      pendingColor,
          points:     pendingShape,
        },
        getAuthHeader()
      );
      setStrings(prev => [...prev, res.data.string]);
      cancelPendingShape();
    } catch (err) {
      console.error('[Sites] save string error:', err);
      alert('Failed to save string');
    } finally {
      setSavingString(false);
    }
  };

  const deleteString = async (stringId) => {
    if (!window.confirm('Delete this string?')) return;
    try {
      await axios.delete(`${BASE}/mideast/strings/${stringId}`, getAuthHeader());
      setStrings(prev => prev.filter(s => s.id !== stringId));
    } catch (err) {
      alert('Failed to delete string');
    }
  };

  // ── Pins ────────────────────────────────────────────────────────
  const removePin = (localId, e) => {
    e.stopPropagation();
    setPins(prev => prev.filter(p => p._localId !== localId));
  };
  const updatePin = (localId, field, value) => {
    setPins(prev => prev.map(p => p._localId === localId ? { ...p, [field]: value } : p));
  };
  const savePins = async () => {
    if (!diagram) return;
    if (pins.some(p => !p.label.trim())) { alert('All pins must have a label'); return; }
    setSavingPins(true);
    try {
      await axios.put(
        `${BASE}/mideast/diagrams/${diagram.id}/points`,
        { points: pins.map((p, i) => ({ label: p.label, description: p.description || '', x_percent: p.x_percent, y_percent: p.y_percent, order_index: i })) },
        getAuthHeader()
      );
      alert('Pins saved');
    } catch (err) {
      alert('Failed to save pins');
    } finally {
      setSavingPins(false);
    }
  };

  // ── Diagram upload / delete ─────────────────────────────────────
  const handleDiagramUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selected) return;
    setUploadingDiag(true);
    try {
      const fd = new FormData();
      fd.append('diagram', file);
      fd.append('site_id', selected.id);
      fd.append('title', selected.site_name);
      const res = await axios.post(`${BASE}/mideast/diagrams`, fd, getMultipartHeader());
      setDiagram(res.data.diagram);
      setPins([]); setStrings([]);
      fetchAll();
    } catch (err) {
      alert('Failed to upload diagram');
    } finally {
      setUploadingDiag(false);
    }
  };

  const handleDeleteDiagram = async () => {
    if (!diagram || !window.confirm('Remove this diagram and all its pins and strings?')) return;
    try {
      await axios.delete(`${BASE}/mideast/diagrams/${diagram.id}`, getAuthHeader());
      setDiagram(null); setPins([]); setStrings([]);
      fetchAll();
    } catch (err) {
      alert('Failed to delete diagram');
    }
  };

  // ── Site CRUD ───────────────────────────────────────────────────
  const openCreate = () => { setEditSite(null); setForm(EMPTY_FORM); setError(''); setShowForm(true); };
  const openEdit   = (site, e) => {
    e.stopPropagation();
    setEditSite(site);
    setForm({ client_id: site.client_id || '', site_name: site.site_name || '', location: site.location || '', system_size: site.system_size || '', panel_count: site.panel_count || '', notes: site.notes || '' });
    setError(''); setShowForm(true);
  };
  const handleSave = async () => {
    if (!form.client_id || !form.site_name.trim()) { setError('Client and site name are required'); return; }
    setSaving(true); setError('');
    try {
      if (editSite) await axios.put(`${BASE}/mideast/sites/${editSite.id}`, form, getAuthHeader());
      else await axios.post(`${BASE}/mideast/sites`, form, getAuthHeader());
      setShowForm(false); fetchAll();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to save site');
    } finally {
      setSaving(false);
    }
  };
  const handleDelete = async (site, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete ${site.site_name}?`)) return;
    try {
      await axios.delete(`${BASE}/mideast/sites/${site.id}`, getAuthHeader());
      fetchAll();
      if (selected?.id === site.id) setSelected(null);
    } catch (err) {
      alert(err?.response?.data?.error || 'Delete failed');
    }
  };

  const filtered = sites.filter(s => {
    const matchSearch = s.site_name?.toLowerCase().includes(search.toLowerCase())
      || s.location?.toLowerCase().includes(search.toLowerCase())
      || s.client_name?.toLowerCase().includes(search.toLowerCase());
    return matchSearch && (!filterClient || String(s.client_id) === filterClient);
  });

  const baseUrl = BASE ? BASE.replace('/api', '') : '';

  // Build SVG polygon point strings
  const svgPts = (pts) => pts.map(p => `${p.x},${p.y}`).join(' ');

  // Is cursor near first draft point?
  const nearFirstPoint = draftPoints.length >= 3 && mousePos
    ? Math.sqrt((mousePos.x - draftPoints[0].x) ** 2 + (mousePos.y - draftPoints[0].y) ** 2) < 4
    : false;

  return (
    <>
      <style>{STYLES}</style>

      {/* Header */}
      <div className="ms-header">
        <div>
          <div className="md-page-title">Sites</div>
          <div className="md-page-sub">{sites.length} registered solar site{sites.length !== 1 ? 's' : ''}</div>
        </div>
        <button className="md-btn md-btn-primary" onClick={openCreate}>
          <Plus size={14}/> Add Site
        </button>
      </div>

      {/* Toolbar */}
      <div className="ms-toolbar">
        <div className="ms-search-wrap">
          <Search size={14} className="ms-search-icon"/>
          <input className="ms-search" placeholder="Search sites…" value={search} onChange={e => setSearch(e.target.value)}/>
        </div>
        <select className="ms-filter-select" value={filterClient} onChange={e => setFilterClient(e.target.value)}>
          <option value="">All Clients</option>
          {clients.map(c => <option key={c.id} value={String(c.id)}>{c.client_name}</option>)}
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="md-skeleton" style={{ height: 300, borderRadius: 14 }}/>
      ) : filtered.length === 0 ? (
        <div className="md-empty">
          <div className="md-empty-icon"><MapPin size={28}/></div>
          {search || filterClient ? 'No sites match your filters' : 'No sites yet'}
        </div>
      ) : (
        <div className="ms-table-wrap">
          <table className="md-table">
            <thead>
              <tr><th>Site Name</th><th>Client</th><th>Location</th><th>System Size</th><th>Panels</th><th>Diagram</th><th></th></tr>
            </thead>
            <tbody>
              {filtered.map(site => (
                <tr key={site.id} style={{ cursor: 'pointer' }} onClick={() => openDetail(site)}>
                  <td style={{ fontWeight: 600, color: '#E2E8F0' }}>{site.site_name}</td>
                  <td style={{ color: 'rgba(255,255,255,0.45)', fontSize: 12 }}>{site.client_name}</td>
                  <td style={{ color: 'rgba(255,255,255,0.40)', fontSize: 12 }}>{site.location || '—'}</td>
                  <td>{site.system_size ? <span className="md-badge md-badge-gold">{site.system_size}</span> : <span style={{ color: 'rgba(255,255,255,0.20)' }}>—</span>}</td>
                  <td style={{ fontFamily: 'DM Mono', fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>{site.panel_count || '—'}</td>
                  <td>
                    <span className={`ms-diagram-badge ${site.has_diagram ? 'ms-diagram-yes' : 'ms-diagram-no'}`}>
                      {site.has_diagram ? <><CheckCircle2 size={9}/> Ready</> : <><Image size={9}/> None</>}
                    </span>
                  </td>
                  <td>
                    <div className="ms-row-actions">
                      <button className="md-btn md-btn-ghost" style={{ padding: '5px 8px' }} onClick={e => openEdit(site, e)}><Edit2 size={12}/></button>
                      <button className="md-btn md-btn-danger" style={{ padding: '5px 8px' }} onClick={e => handleDelete(site, e)}><Trash2 size={12}/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Detail Panel ── */}
      {selected && (
        <>
          <div className="ms-panel-overlay" onClick={() => setSelected(null)}/>
          <div className="ms-panel">
            <div className="ms-panel-header">
              <div style={{ width: 40, height: 40, borderRadius: 11, background: 'rgba(245,158,11,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FCD34D', flexShrink: 0 }}>
                <MapPin size={18}/>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="ms-panel-title">{selected.site_name}</div>
                <div className="ms-panel-sub">{selected.client_name}</div>
              </div>
              <button className="md-modal-close" onClick={() => setSelected(null)}><X size={18}/></button>
            </div>

            <div className="ms-panel-tabs">
              {['info', 'diagram'].map(tab => (
                <button key={tab} className={`ms-panel-tab${activeTab === tab ? ' active' : ''}`} onClick={() => setActiveTab(tab)}>
                  {tab === 'info' ? 'Site Info' : 'Diagram & Strings'}
                </button>
              ))}
            </div>

            <div className="ms-panel-body">

              {/* INFO TAB */}
              {activeTab === 'info' && (
                <>
                  <div className="ms-info-grid">
                    {[
                      { label: 'Location',    value: selected.location,    icon: <MapPin size={13}/> },
                      { label: 'System Size', value: selected.system_size, icon: <Zap size={13}/> },
                      { label: 'Panel Count', value: selected.panel_count, icon: <LayoutGrid size={13}/> },
                      { label: 'Client',      value: selected.client_name, icon: <Building2 size={13}/> },
                    ].map(({ label, value, icon }) => (
                      <div key={label} className="ms-info-item">
                        <div className="ms-info-item-label">{label}</div>
                        <div className="ms-info-item-value" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ color: 'rgba(14,165,233,0.60)' }}>{icon}</span>
                          {value || <span style={{ color: 'rgba(255,255,255,0.20)' }}>—</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                  {selected.notes && (
                    <div className="ms-info-item" style={{ marginBottom: 16 }}>
                      <div className="ms-info-item-label">Notes</div>
                      <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', lineHeight: 1.6, marginTop: 4 }}>{selected.notes}</div>
                    </div>
                  )}
                  <button className="md-btn md-btn-ghost" style={{ width: '100%', justifyContent: 'center', marginTop: 8 }} onClick={e => openEdit(selected, e)}>
                    <Edit2 size={13}/> Edit Site Details
                  </button>
                </>
              )}

              {/* DIAGRAM TAB */}
              {activeTab === 'diagram' && (
                <>
                  {diagramLoading ? (
                    <div className="md-skeleton" style={{ height: 200, borderRadius: 10 }}/>
                  ) : !diagram ? (
                    <>
                      <div className="ms-diagram-upload-zone" onClick={() => diagramInputRef.current?.click()}>
                        {uploadingDiag ? (
                          <div style={{ textAlign: 'center' }}>
                            <span className="md-spinner"/>
                            <div style={{ marginTop: 12, fontSize: 13, color: 'rgba(255,255,255,0.40)' }}>Uploading…</div>
                          </div>
                        ) : (
                          <>
                            <div className="ms-diagram-upload-icon"><Upload size={22}/></div>
                            <div className="ms-diagram-upload-title">Upload Site Diagram</div>
                            <div className="ms-diagram-upload-sub">PNG, JPG or PDF — max 20MB</div>
                          </>
                        )}
                      </div>
                      <input ref={diagramInputRef} type="file" accept="image/*,.pdf" style={{ display: 'none' }} onChange={handleDiagramUpload}/>
                    </>
                  ) : (
                    <>
                      {/* Action row */}
                      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                        <button className="md-btn md-btn-ghost" style={{ fontSize: 11 }} onClick={() => diagramInputRef.current?.click()}><Upload size={12}/> Replace</button>
                        <button className="md-btn md-btn-danger" style={{ fontSize: 11 }} onClick={handleDeleteDiagram}><Trash2 size={12}/> Remove</button>
                        {diagramMode === 'pins' && (
                          <button className="md-btn md-btn-primary" style={{ fontSize: 11, marginLeft: 'auto' }} onClick={savePins} disabled={savingPins}>
                            {savingPins ? <><span className="md-spinner"/> Saving…</> : <><CheckCircle2 size={12}/> Save Pins</>}
                          </button>
                        )}
                      </div>
                      <input ref={diagramInputRef} type="file" accept="image/*,.pdf" style={{ display: 'none' }} onChange={handleDiagramUpload}/>

                      {/* Mode toggle */}
                      <div className="ms-mode-toggle">
                        <button className={`ms-mode-btn${diagramMode === 'pins' ? ' active-pins' : ''}`} onClick={() => { setDiagramMode('pins'); cancelDraft(); cancelPendingShape(); }}>
                          <Move size={12}/> Pins {pins.length > 0 && <span style={{ opacity: 0.65 }}>({pins.length})</span>}
                        </button>
                        <button className={`ms-mode-btn${diagramMode === 'strings' ? ' active-strings' : ''}`} onClick={() => setDiagramMode('strings')}>
                          <BoxSelect size={12}/> Strings {strings.length > 0 && <span style={{ opacity: 0.65 }}>({strings.length})</span>}
                        </button>
                      </div>

                      {/* Canvas hint */}
                      <div className="ms-canvas-hint">
                        {diagramMode === 'pins'    && <><Move size={12}/> Click to place a pin</>}
                        {diagramMode === 'strings' && draftPoints.length === 0 && !pendingShape && <><BoxSelect size={12}/> Click to start placing polygon points</>}
                        {diagramMode === 'strings' && draftPoints.length > 0   && !pendingShape && <><BoxSelect size={12}/> {draftPoints.length} point{draftPoints.length > 1 ? 's' : ''} — {draftPoints.length >= 3 ? 'click green dot or Finish to close' : `need ${3 - draftPoints.length} more`}</>}
                        {diagramMode === 'strings' && pendingShape              && <>Label the shape below</>}
                      </div>

                      {/* Draft in-progress toolbar */}
                      {diagramMode === 'strings' && draftPoints.length > 0 && !pendingShape && (
                        <div className="ms-draft-toolbar">
                          <span style={{ flex: 1 }}>{draftPoints.length} point{draftPoints.length !== 1 ? 's' : ''} placed</span>
                          {draftPoints.length >= 3 && (
                            <button className="md-btn md-btn-primary" style={{ fontSize: 11, padding: '5px 12px' }} onClick={finishShape}>
                              <CheckCircle2 size={11}/> Finish Shape
                            </button>
                          )}
                          <button className="md-btn md-btn-ghost" style={{ fontSize: 11, padding: '5px 10px' }} onClick={cancelDraft}>
                            <X size={11}/> Cancel
                          </button>
                        </div>
                      )}

                      {/* Canvas */}
                      <div
                        className="ms-pin-canvas-wrap"
                        ref={canvasRef}
                        onClick={handleCanvasClick}
                        onMouseMove={handleCanvasMouseMove}
                        onMouseLeave={() => setMousePos(null)}
                      >
                        <img src={`${baseUrl}${diagram.diagram_url}`} alt="Site diagram" className="ms-pin-canvas-img" draggable={false}/>

                        {/* SVG layer for polygons + draft */}
                        <svg
                          className="ms-svg-overlay"
                          viewBox="0 0 100 100"
                          preserveAspectRatio="none"
                        >
                          {/* Saved strings */}
                          {strings.map(s => {
                            const pts = Array.isArray(s.points) ? s.points : [];
                            if (pts.length < 3) return null;
                            return (
                              <polygon
                                key={s.id}
                                points={svgPts(pts)}
                                fill={`${s.color}28`}
                                stroke={s.color}
                                strokeWidth="0.5"
                                strokeOpacity="0.85"
                              />
                            );
                          })}

                          {/* Draft polygon edges */}
                          {draftPoints.length >= 2 && (
                            <polyline
                              points={svgPts(draftPoints)}
                              fill="none"
                              stroke="rgba(255,255,255,0.60)"
                              strokeWidth="0.45"
                              strokeDasharray="2,1"
                            />
                          )}

                          {/* Preview line from last point to cursor */}
                          {draftPoints.length > 0 && mousePos && (
                            <line
                              x1={draftPoints[draftPoints.length - 1].x}
                              y1={draftPoints[draftPoints.length - 1].y}
                              x2={mousePos.x}
                              y2={mousePos.y}
                              stroke="rgba(255,255,255,0.35)"
                              strokeWidth="0.4"
                              strokeDasharray="1.5,1"
                            />
                          )}

                          {/* Draft vertex dots */}
                          {draftPoints.map((p, i) => (
                            <circle
                              key={i}
                              cx={p.x} cy={p.y}
                              r={i === 0 ? '1.4' : '0.75'}
                              fill={i === 0 ? (nearFirstPoint ? '#22D3A5' : '#FFFFFF') : 'rgba(255,255,255,0.80)'}
                              stroke={i === 0 ? '#22D3A5' : 'none'}
                              strokeWidth={i === 0 ? '0.5' : '0'}
                            />
                          ))}

                          {/* Close indicator ring around first point */}
                          {nearFirstPoint && (
                            <circle
                              cx={draftPoints[0].x} cy={draftPoints[0].y}
                              r="3" fill="none"
                              stroke="#22D3A5" strokeWidth="0.5" opacity="0.75"
                            />
                          )}
                        </svg>

                        {/* String centroid labels */}
                        {strings.map(s => {
                          const pts = Array.isArray(s.points) ? s.points : [];
                          if (pts.length < 3) return null;
                          const c = centroid(pts);
                          return (
                            <span
                              key={`lbl-${s.id}`}
                              className="ms-string-label-chip"
                              style={{ left: `${c.x}%`, top: `${c.y}%`, color: s.color }}
                            >
                              {s.label}
                            </span>
                          );
                        })}

                        {/* Pins */}
                        {pins.map((pin, idx) => (
                          <div key={pin._localId} className="ms-pin" style={{ left: `${pin.x_percent}%`, top: `${pin.y_percent}%` }} onClick={e => e.stopPropagation()}>
                            <div className="ms-pin-dot ms-pin-dot-gold">{idx + 1}</div>
                            <div className="ms-pin-tooltip">{pin.label || `Pin ${idx + 1}`}</div>
                            {diagramMode === 'pins' && (
                              <button className="ms-pin-remove" onClick={e => removePin(pin._localId, e)}>×</button>
                            )}
                          </div>
                        ))}
                      </div>

                      {/* Pending shape label form */}
                      {diagramMode === 'strings' && pendingShape && (
                        <div className="ms-pending-form">
                          <div className="ms-pending-form-title">New String — {pendingShape.length} Points</div>
                          <input
                            className="md-input"
                            placeholder='Label (e.g. "String 1", "South Array")'
                            value={pendingLabel}
                            onChange={e => setPendingLabel(e.target.value)}
                            style={{ marginBottom: 12 }}
                            autoFocus
                            onKeyDown={e => { if (e.key === 'Enter') saveString(); if (e.key === 'Escape') cancelPendingShape(); }}
                          />
                          <div className="ms-color-row">
                            <span style={{ fontSize: 10, fontWeight: 700, fontFamily: 'DM Mono', color: 'rgba(255,255,255,0.30)', letterSpacing: '0.12em', textTransform: 'uppercase', flexShrink: 0 }}>Colour</span>
                            {STRING_COLORS.map(c => (
                              <div key={c} className={`ms-color-swatch${pendingColor === c ? ' selected' : ''}`} style={{ background: c }} onClick={() => setPendingColor(c)}/>
                            ))}
                          </div>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button className="md-btn md-btn-ghost" style={{ fontSize: 11 }} onClick={cancelPendingShape}>Cancel</button>
                            <button className="md-btn md-btn-primary" style={{ fontSize: 11 }} onClick={saveString} disabled={savingString || !pendingLabel.trim()}>
                              {savingString ? <><span className="md-spinner"/> Saving…</> : <><Plus size={12}/> Save String</>}
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Strings list */}
                      {diagramMode === 'strings' && (
                        strings.length === 0 && !pendingShape && draftPoints.length === 0 ? (
                          <div className="md-empty" style={{ padding: '20px 0' }}>
                            <div className="md-empty-icon"><BoxSelect size={22}/></div>
                            No strings yet — click on the diagram to start placing points
                          </div>
                        ) : strings.length > 0 ? (
                          <>
                            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.25)', fontFamily: 'DM Mono', marginTop: 20, marginBottom: 10 }}>
                              Defined Strings ({strings.length})
                            </div>
                            <div className="ms-string-list">
                              {strings.map(s => (
                                <div key={s.id} className="ms-string-item">
                                  <div className="ms-string-color-block" style={{ background: s.color }}/>
                                  <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#E2E8F0' }}>{s.label}</span>
                                  <span style={{ fontSize: 10, fontFamily: 'DM Mono', color: 'rgba(255,255,255,0.22)' }}>
                                    {Array.isArray(s.points) ? `${s.points.length} pts` : ''}
                                  </span>
                                  <button className="md-btn md-btn-danger" style={{ padding: '4px 7px' }} onClick={() => deleteString(s.id)}><Trash2 size={11}/></button>
                                </div>
                              ))}
                            </div>
                          </>
                        ) : null
                      )}

                      {/* Pins list (pins mode) */}
                      {diagramMode === 'pins' && pins.length > 0 && (
                        <>
                          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.25)', fontFamily: 'DM Mono', marginTop: 20, marginBottom: 10 }}>
                            Label Pins ({pins.length})
                          </div>
                          <div className="ms-pin-list">
                            {pins.map((pin, idx) => (
                              <div key={pin._localId} className="ms-pin-list-item">
                                <div className="ms-pin-list-num">{idx + 1}</div>
                                <div style={{ flex: 1 }}>
                                  <input className="ms-pin-label-input" placeholder={`Pin ${idx + 1} label`} value={pin.label} onChange={e => updatePin(pin._localId, 'label', e.target.value)}/>
                                  <input className="ms-pin-desc-input" placeholder="Description (optional)" value={pin.description} onChange={e => updatePin(pin._localId, 'description', e.target.value)}/>
                                </div>
                                <button className="md-btn md-btn-danger" style={{ padding: '4px 7px' }} onClick={e => removePin(pin._localId, e)}><X size={11}/></button>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      )}

      {/* Create / Edit Modal */}
      {showForm && (
        <div className="md-modal-overlay">
          <div className="md-modal">
            <div className="md-modal-header">
              <MapPin size={16} color="rgba(245,158,11,0.70)" strokeWidth={1.8}/>
              <div className="md-modal-title">{editSite ? 'Edit Site' : 'New Site'}</div>
              <button className="md-modal-close" onClick={() => setShowForm(false)}><X size={18}/></button>
            </div>
            <div className="md-modal-body">
              {error && <div className="md-alert md-alert-error">⚠ {error}</div>}
              <div className="ms-form-grid">
                <div className="md-field ms-form-full">
                  <label className="md-label">Client *</label>
                  <select className="md-input" value={form.client_id} onChange={e => setForm(f => ({ ...f, client_id: e.target.value }))} style={{ cursor: 'pointer' }}>
                    <option value="">Select client…</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.client_name}</option>)}
                  </select>
                </div>
                <div className="md-field ms-form-full">
                  <label className="md-label">Site Name *</label>
                  <input className="md-input" placeholder="e.g. Al Reem Solar Farm" value={form.site_name} onChange={e => setForm(f => ({ ...f, site_name: e.target.value }))}/>
                </div>
                <div className="md-field">
                  <label className="md-label">Location</label>
                  <input className="md-input" placeholder="City, Country" value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))}/>
                </div>
                <div className="md-field">
                  <label className="md-label">System Size</label>
                  <input className="md-input" placeholder="e.g. 500 kWp" value={form.system_size} onChange={e => setForm(f => ({ ...f, system_size: e.target.value }))}/>
                </div>
                <div className="md-field">
                  <label className="md-label">Panel Count</label>
                  <input className="md-input" type="number" placeholder="e.g. 1200" value={form.panel_count} onChange={e => setForm(f => ({ ...f, panel_count: e.target.value }))}/>
                </div>
                <div className="md-field ms-form-full">
                  <label className="md-label">Notes</label>
                  <textarea className="md-input" placeholder="Any additional site notes…" rows={3} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} style={{ resize: 'vertical' }}/>
                </div>
              </div>
            </div>
            <div className="md-modal-footer">
              <button className="md-btn md-btn-ghost" onClick={() => setShowForm(false)} disabled={saving}>Cancel</button>
              <button className="md-btn md-btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? <><span className="md-spinner"/> Saving…</> : editSite ? 'Save Changes' : 'Create Site'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MideastSites;